import React, { Fragment, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Navbar.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import {
  MenuItem,
  SalvAccordion,
  SalvAvatar,
  SalvIconButton,
  SalvMenuButton,
  SalvProgressCircle,
  SalvSearchField,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { InternalNavigation, NavbarItemType, NavbarSubItemType } from './helper';
import { faRightFromBracket, faUser, faComments, faBookOpen, faSidebar } from '@fortawesome/pro-regular-svg-icons';
import { useMe } from '../organisation/useMe';
import { useNavigate, useLocation, Location } from 'react-router';
import { logoutAndDeleteToken } from '../auth/authReducer';
import { useDispatch } from 'react-redux';
import { useIntercom } from 'react-use-intercom';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { useSwitchOrganisationForm } from '../auth/authActions';
import { SalvLogo } from '../common/SalvLogo';
import useVisibleNavbarItems from './useVisibleNavbarItems';
import { clsx } from 'clsx';
import { CSSTransition } from 'react-transition-group';

export interface Props {
  unreadIntercomMessagesCount: number;
}

const Navbar = ({ unreadIntercomMessagesCount }: Props) => {
  const { isGlobalAdmin, currentOrganisation, availableOrganisations, name, email, features } = useMe();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { switchOrganisationV2, isLoading: isOrganisationSwitchLoading } = useSwitchOrganisationForm();
  const { showMessages: showIntercomMessages, show: showIntercom } = useIntercom();
  const location = useLocation();

  const isWhiteLabel = isFeatureEnabled(ORGANISATION_FEATURE.WHITE_LABEL, features);

  const [isMinimized, setMinimized] = useState(false);
  const toggleMinimization = () => {
    setMinimized((prev) => !prev);
    setSearchText('');
  };

  const organisationSectionRef = useRef<HTMLDivElement>(null);
  const profileSectionRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  const openProfileMenu = useCallback(() => {
    profileButtonRef.current?.click();
  }, [profileButtonRef]);

  const { visibleNavbarItems } = useVisibleNavbarItems();
  const [searchText, setSearchText] = useState('');

  const filteredNavbarItems = useMemo(
    () =>
      visibleNavbarItems
        .flatMap((item) => {
          if (!searchText) {
            return item;
          }

          const flattenedSubItems: NavbarItemType[] | undefined = item.subItems?.map((subItem) => ({
            ...subItem,
            icon: item.icon,
          }));

          return flattenedSubItems || item;
        })
        .filter((item) => !searchText || item.title.toLowerCase().includes(searchText.toLowerCase())),
    [visibleNavbarItems, searchText]
  );

  return (
    <div
      className={'salv-navbar'}
      style={{
        width: isMinimized ? '64px' : '240px',
      }}
    >
      <div className={'salv-logo-section'}>
        <SalvLogo
          small={isMinimized}
          className={isMinimized ? 'salv-logo-small' : 'salv-logo-big'}
          whiteLabel={isWhiteLabel}
        />
      </div>
      <CSSTransition
        in={!isMinimized}
        nodeRef={organisationSectionRef}
        timeout={150}
        classNames={'organisation-section'}
      >
        <div ref={organisationSectionRef} className={'organisation-section m-b-16'}>
          {!isMinimized &&
            (isGlobalAdmin ? (
              <SalvMenuButton
                label={
                  isOrganisationSwitchLoading ? (
                    <SalvProgressCircle />
                  ) : currentOrganisation ? (
                    currentOrganisation.name
                  ) : (
                    'No organisation'
                  )
                }
                onAction={async ([value]) => {
                  switch (value) {
                    case 'clear':
                      await switchOrganisationV2(undefined);
                      break;
                    default:
                      await switchOrganisationV2(Number(value));
                  }
                }}
                enableFilter
                className={'organisation-menu-button'}
              >
                {availableOrganisations.map((organisation, i) => (
                  <MenuItem
                    title={organisation.name}
                    value={organisation.id.toString()}
                    key={organisation.id}
                    sectionEnd={i === availableOrganisations.length - 1}
                  />
                ))}
                <MenuItem title={'Clear'} dangerTextColor value={'clear'} />
              </SalvMenuButton>
            ) : (
              <div className={'organisation-name'}>{currentOrganisation?.name}</div>
            ))}
          <SalvIconButton
            icon={faSidebar}
            ariaLabel={'Minimize Navbar'}
            onClick={toggleMinimization}
            className={'minimize-button'}
          />
        </div>
      </CSSTransition>

      <div className={'search-section'}>
        {!isMinimized && (
          <SalvSearchField
            placeholder={'Search menu...'}
            size={Size.SMALL}
            onChange={(value) => setSearchText(value)}
            ariaLabel={'Search Navbar'}
            className={'p-y-8'}
          />
        )}
      </div>
      <div className={'salv-navbar-items m-b-16'}>
        {filteredNavbarItems.map((item) => (
          <Fragment key={item.title + item.icon.iconName}>
            <SalvTooltipWrapper tooltip={item.title} isDisabled={!isMinimized} placement={'right'} offset={12}>
              <NavbarItem
                title={item.title}
                link={item.link}
                icon={item.icon}
                subItems={item.subItems}
                isMinimized={isMinimized}
                toggleMinimization={toggleMinimization}
                location={location}
              >
                {item.subItems?.map((subItem) => (
                  <NavbarSubItem title={subItem.title} link={subItem.link} key={subItem.title} location={location} />
                ))}
              </NavbarItem>
            </SalvTooltipWrapper>
            {item.isSectionEnd && <div className={'horizontal-separator m-y-16'} />}
          </Fragment>
        ))}
      </div>
      <div className={'horizontal-separator m-b-8'} />

      <CSSTransition in={!isMinimized} nodeRef={profileSectionRef} timeout={150} classNames={'profile-section'}>
        <div ref={profileSectionRef} className={'profile-section'}>
          {isMinimized ? (
            <SalvAvatar
              email={email}
              onClick={() => {
                toggleMinimization();
                setTimeout(() => openProfileMenu(), 100);
              }}
            />
          ) : (
            <SalvMenuButton
              label={name}
              avatarEmail={email}
              onAction={([value]) => {
                switch (value) {
                  case 'profile-settings':
                    navigate(InternalNavigation.profile);
                    break;
                  case 'message-support':
                    if (unreadIntercomMessagesCount > 0) {
                      showIntercomMessages();
                    } else {
                      showIntercom();
                    }
                    break;
                  case 'help-center':
                    window.open('https://help.salv.com/', '_blank');
                    break;
                  case 'sign-out':
                    dispatch(logoutAndDeleteToken());
                    break;
                }
              }}
              placement={'top'}
              className={'profile-menu-button'}
              ref={profileButtonRef}
            >
              <MenuItem title={'Profile Settings'} value={'profile-settings'} icon={faUser} />
              {isFeatureEnabled(ORGANISATION_FEATURE.INTERCOM, features) && (
                <MenuItem title={'Message Support'} value={'message-support'} icon={faComments} />
              )}
              <MenuItem title={'Help Center'} value={'help-center'} icon={faBookOpen} sectionEnd />
              <MenuItem title={'Sign Out'} value={'sign-out'} icon={faRightFromBracket} />
            </SalvMenuButton>
          )}
        </div>
      </CSSTransition>

      <div className={'salv-version-section m-t-8'}>
        {!isMinimized && (
          <div className={'salv-version'}>
            {!isWhiteLabel && 'Salv Platform. '}Version {process.env.FE_APP_VERSION || 'unknown'}
          </div>
        )}
      </div>
    </div>
  );
};

interface NavbarItemProps extends PropsWithChildren<NavbarItemType> {
  isMinimized: boolean;
  toggleMinimization: () => void;
  location: Location;
}

const NavbarItem = ({
  title,
  link,
  icon,
  children,
  isMinimized,
  toggleMinimization,
  subItems,
  location,
}: NavbarItemProps) => {
  const [isExpanded, setExpanded] = useState(false);
  const toggleExpansion = () => setExpanded((prev) => !prev);

  const isGroupItem = !!children;

  const NavbarItemTitle = () => {
    const TitleWrapper = ({ children: c }: PropsWithChildren) =>
      isGroupItem ? (
        <div className={'salv-navbar-item-title' + (isMinimized ? ' salv-navbar-item-hover-area' : '')}>{c}</div>
      ) : (
        <Link to={link!} className={'salv-navbar-item-title salv-navbar-item-hover-area'}>
          {c}
        </Link>
      );

    return (
      <TitleWrapper>
        <FontAwesomeIcon icon={icon} className={'salv-icon'} />
        {!isMinimized && <span>{title}</span>}
      </TitleWrapper>
    );
  };

  const isActive = location.pathname === link;

  const isChildActive = subItems?.some((subItem) => location.pathname === subItem.link);
  useEffect(() => {
    if (isChildActive && !isMinimized) {
      setExpanded(true);
    }
  }, [isChildActive, isMinimized]);

  useEffect(() => {
    if (isMinimized) {
      setExpanded(false);
    }
  }, [isMinimized]);

  return (
    <div
      className={clsx('salv-navbar-item', {
        'salv-navbar-item-active': isActive,
      })}
      style={{
        width: isMinimized ? '48px' : '224px',
      }}
      onClick={() => {
        if (isGroupItem && isMinimized) {
          toggleMinimization();
          toggleExpansion();
        }
      }}
    >
      {isGroupItem && !isMinimized ? (
        <SalvAccordion
          label={<NavbarItemTitle />}
          ariaLabel={title}
          size={Size.SMALL}
          isExpanded={isExpanded}
          onToggle={toggleExpansion}
        >
          {children}
        </SalvAccordion>
      ) : (
        <NavbarItemTitle />
      )}
    </div>
  );
};

interface NavbarSubItemProps extends NavbarSubItemType {
  location: Location;
}

const NavbarSubItem = ({ title, link, location }: NavbarSubItemProps) => {
  const isActive = location.pathname === link;
  return (
    <div
      className={clsx('salv-navbar-sub-item', {
        'salv-navbar-item-active': isActive,
      })}
    >
      <Link to={link} className={'salv-navbar-item-title salv-navbar-item-hover-area'}>
        {title}
      </Link>
    </div>
  );
};

export default Navbar;
